/**
 * versona_census — the world-wide behavioral census behind the versona↔human
 * gap analysis.
 *
 * Where versona_lab stages scenarios on ONE cast, the census reads the WHOLE
 * population's tables and reduces them to distributions a social scientist
 * would ask for: relationship-network shape (degree, familiarity layers vs
 * Dunbar's 5/15/50), emotion demographics (OCC-type histogram, PAD spread,
 * grudge load), survival state (needs, food security, wealth Gini), political
 * participation (charters, norms, deliberations, rivalries, wars, offices),
 * group structure (households, kinship, generations, migration, civs), and
 * information transport (memory kinds, rumor share, per-factKey propagation
 * breadth — how far one fact traveled).
 *
 * Pure + conn-structural like versona_lab: the same code serves the
 * `?probe=versonacensus` suite and the `verify:versonacensus` harness.
 * Everything is a read — the census never mutates the world.
 */

import { tbl } from './versona_lab.js';

// ─── Small stats helpers (exported for harness pure-checks) ──────────────────

export interface Stats { n: number; mean: number; p50: number; p90: number; max: number }

export function stats(values: number[]): Stats {
  if (values.length === 0) return { n: 0, mean: 0, p50: 0, p90: 0, max: 0 };
  const s = [...values].sort((a, b) => a - b);
  const q = (p: number): number => s[Math.min(s.length - 1, Math.floor(p * s.length))];
  return {
    n: s.length,
    mean: +(s.reduce((a, b) => a + b, 0) / s.length).toFixed(2),
    p50: q(0.5), p90: q(0.9), max: s[s.length - 1],
  };
}

/** Gini coefficient over non-negative values (0 = perfect equality). */
export function gini(values: number[]): number {
  const s = values.filter((v) => v >= 0).sort((a, b) => a - b);
  if (s.length === 0) return 0;
  const sum = s.reduce((a, b) => a + b, 0);
  if (sum === 0) return 0;
  let cum = 0, lorenz = 0;
  for (const v of s) { cum += v; lorenz += cum; }
  return +((s.length + 1 - (2 * lorenz) / sum) / s.length).toFixed(3);
}

export function hist<T extends string | number>(values: T[]): Record<string, number> {
  const h: Record<string, number> = {};
  for (const v of values) h[String(v)] = (h[String(v)] ?? 0) + 1;
  return h;
}

export function bucketHist(values: number[], edges: number[]): Record<string, number> {
  const h: Record<string, number> = {};
  for (const v of values) {
    let label = `≥${edges[edges.length - 1]}`;
    for (let i = 0; i < edges.length; i++) {
      if (v < edges[i]) { label = i === 0 ? `<${edges[0]}` : `${edges[i - 1]}–${edges[i] - 1}`; break; }
    }
    h[label] = (h[label] ?? 0) + 1;
  }
  return h;
}

// ─── Row shapes (census-only tables; lab covers the rest) ────────────────────

interface Ts { microsSinceUnixEpoch: bigint }
interface AgentRow { versonaId: bigint; settlementId: bigint; job: string; sociability: number; wallet: bigint; hunger: number; needRest: number; needSocial: number; mood: number; carry: number }
interface PairRow { aId: bigint; bId: bigint; familiarity: number }
interface EmotionRow { versonaId: bigint; etype: string; targetId: bigint; intensity: number; attendantOf: bigint }
interface GrudgeRow { versonaId: bigint; targetId: bigint; intensity: number; offenses: number }
interface MoodRow { versonaId: bigint; pleasureX10: number; arousalX10: number; dominanceX10: number }
interface MindRow { versonaId: bigint; hungerStreak: number }
interface PantryRow { versonaId: bigint; foodDays: number }
interface FavorRow { aId: bigint; bId: bigint; balance: number }
interface EncounterRow { kind: string; state: string; tier: string }
interface ChatRow { speaker: string; answered: boolean }
interface MemoryRow { versonaId: bigint; kind: string; factKey: string; importance: number }
interface LineageRow { childId: bigint; generation: number }
interface HouseholdRow { householdId: bigint; settlementId: bigint }
interface HouseholdMemberRow { householdId: bigint; versonaId: bigint }
interface KinshipRow { kind: string }
interface MigrationRow { reason: string }
interface GraveRow { cause: string }
interface EconRow { settlementId: bigint; taxBps: number; treasury: bigint; mayorId: bigint }
interface ClauseRow { kind: string; status: string; yesVotes: number; noVotes: number }
interface NormRow { active: boolean; adoptionX10: number }
interface DelibRow { kind: string; status: string }
interface RivalryRow { grievance: number }
interface WarRow { status: string }
interface OfficeRow { office: string }
interface CivRow { civ: string }
interface StandingRow { regard: number }
interface InjuryRow { hp: number }

// ─── The census ──────────────────────────────────────────────────────────────

export interface CensusReport {
  atMs: number;
  population: {
    agents: number; settlements: number; jobs: Record<string, number>;
    generations: Record<string, number>; bornInWorld: number; graves: Record<string, number>;
  };
  social: {
    pairsTotal: number; degree: Stats; familiarityHist: Record<string, number>;
    closeTiesPerAgent: Stats;      // familiarity ≥ 80 (the "support clique" layer)
    friendsPerAgent: Stats;        // familiarity ≥ 50
    maxedShare: number;            // share of pairs sitting at familiarity 100
    encountersLive: number; encounterTiers: Record<string, number>;
    favorEdges: number; openDebts: number;
  };
  emotions: {
    episodes: number; agentsFeeling: number; perFeelingAgent: Stats;
    etypeHist: Record<string, number>; targetedShare: number; resolutionShare: number;
    intensity: Stats;
    pad: { rows: number; pleasure: Stats; arousal: Stats; dominance: Stats };
    grudges: { total: number; holders: number; intensity: Stats; maxOffenses: number };
    hungerStreakActive: number;
  };
  survival: {
    hunger: Stats; needRest: Stats; needSocial: Stats; mood: Stats;
    pantryDays: Stats; foodInsecureShare: number;   // pantry < 0.5 days
    wallet: { stats: Stats; gini: number };
    carryingShare: number; injured: number;
  };
  politics: {
    mayoredShare: number; taxPctHist: Record<string, number>; treasury: Stats;
    clauses: Record<string, number>;               // "kind/status" → n
    norms: { active: number; adoption: Stats };
    delibs: Record<string, number>;                // "kind/status" → n
    rivalries: { n: number; grievance: Stats }; wars: Record<string, number>;
    officesFilled: number;
    factions: Record<string, number>;
    // R5 — persuaded political leans (no row = pure material voting)
    opinions: { rows: number; lean: Stats; strongConvictions: number };
    // POLITY wave — diplomacy, organized parties, recall accountability
    treaties: Record<string, number>;              // "kind/status" → n
    goodwill: { pairs: number; level: Stats };
    parties: { n: number; byFaction: Record<string, number>; strong: Stats };
    recalls: Record<string, number>;               // status → n
  };
  groups: {
    households: { n: number; size: Stats };
    kinship: Record<string, number>;
    migrations: Record<string, number>;
    civSplit: Record<string, number>;
    playerResidents: number;
    // R10 — colony fission (exodus migrations + populated-settlement delta)
    colonists: number;
  };
  information: {
    memories: { total: number; kinds: Record<string, number>; importance: Stats };
    rumorShare: number;                            // second-hand knowledge fraction
    propagation: { breadth: Stats; top: { factKey: string; holders: number; rumorHolders: number }[] };
    playerFacing: { standingRows: number; regard: Stats; chats: number; chatAnsweredShare: number };
    // V12/V13 — lies and player-planted tales in circulation
    deception: { slanders: number; caughtLying: number; playerTales: number };
  };
}

export function computeCensus(conn: unknown, nowMs: number): CensusReport {
  const agents = tbl<AgentRow>(conn, 'npcAgent');
  const ids = new Set(agents.map((a) => a.versonaId));
  const bySettlement = new Map<bigint, number>();
  for (const a of agents) bySettlement.set(a.settlementId, (bySettlement.get(a.settlementId) ?? 0) + 1);

  // population
  const lineage = tbl<LineageRow>(conn, 'versonaLineage');
  const population: CensusReport['population'] = {
    agents: agents.length, settlements: bySettlement.size,
    jobs: hist(agents.map((a) => a.job)),
    generations: hist(lineage.map((l) => l.generation)),
    bornInWorld: lineage.filter((l) => ids.has(l.childId)).length,
    graves: hist(tbl<GraveRow>(conn, 'grave').map((g) => g.cause)),
  };

  // social network
  const pairs = tbl<PairRow>(conn, 'versonaPair');
  const degreeMap = new Map<bigint, number>();
  const closeMap = new Map<bigint, number>();
  const friendMap = new Map<bigint, number>();
  for (const p of pairs) {
    for (const side of [p.aId, p.bId]) {
      degreeMap.set(side, (degreeMap.get(side) ?? 0) + 1);
      if (p.familiarity >= 80) closeMap.set(side, (closeMap.get(side) ?? 0) + 1);
      if (p.familiarity >= 50) friendMap.set(side, (friendMap.get(side) ?? 0) + 1);
    }
  }
  const perAgent = (m: Map<bigint, number>): number[] => agents.map((a) => m.get(a.versonaId) ?? 0);
  const favors = tbl<FavorRow>(conn, 'versonaFavor');
  const encounters = tbl<EncounterRow>(conn, 'versonaEncounter');
  const social: CensusReport['social'] = {
    pairsTotal: pairs.length,
    degree: stats(perAgent(degreeMap)),
    familiarityHist: bucketHist(pairs.map((p) => p.familiarity), [20, 50, 80, 100]),
    closeTiesPerAgent: stats(perAgent(closeMap)),
    friendsPerAgent: stats(perAgent(friendMap)),
    maxedShare: pairs.length ? +(pairs.filter((p) => p.familiarity >= 100).length / pairs.length).toFixed(3) : 0,
    encountersLive: encounters.length,
    encounterTiers: hist(encounters.map((e) => e.tier || e.kind)),
    favorEdges: favors.length,
    openDebts: favors.filter((f) => f.balance !== 0).length,
  };

  // emotions
  const eps = tbl<EmotionRow>(conn, 'versonaEmotion');
  const feelingAgents = new Set(eps.map((e) => e.versonaId));
  const perAgentEp = new Map<bigint, number>();
  for (const e of eps) perAgentEp.set(e.versonaId, (perAgentEp.get(e.versonaId) ?? 0) + 1);
  const moods = tbl<MoodRow>(conn, 'npcMood');
  const grudges = tbl<GrudgeRow>(conn, 'versonaGrudge');
  const emotions: CensusReport['emotions'] = {
    episodes: eps.length, agentsFeeling: feelingAgents.size,
    perFeelingAgent: stats([...perAgentEp.values()]),
    etypeHist: hist(eps.map((e) => e.etype)),
    targetedShare: eps.length ? +(eps.filter((e) => e.targetId !== 0n).length / eps.length).toFixed(3) : 0,
    resolutionShare: eps.length ? +(eps.filter((e) => e.attendantOf !== 0n).length / eps.length).toFixed(3) : 0,
    intensity: stats(eps.map((e) => e.intensity)),
    pad: {
      rows: moods.length,
      pleasure: stats(moods.map((m) => m.pleasureX10 / 10)),
      arousal: stats(moods.map((m) => m.arousalX10 / 10)),
      dominance: stats(moods.map((m) => m.dominanceX10 / 10)),
    },
    grudges: {
      total: grudges.length, holders: new Set(grudges.map((g) => g.versonaId)).size,
      intensity: stats(grudges.map((g) => g.intensity)),
      maxOffenses: Math.max(0, ...grudges.map((g) => g.offenses)),
    },
    hungerStreakActive: tbl<MindRow>(conn, 'npcMind').filter((m) => m.hungerStreak > 0).length,
  };

  // survival
  const pantries = tbl<PantryRow>(conn, 'npcPantry');
  const wallets = agents.map((a) => Number(a.wallet));
  const survival: CensusReport['survival'] = {
    hunger: stats(agents.map((a) => a.hunger)),
    needRest: stats(agents.map((a) => a.needRest)),
    needSocial: stats(agents.map((a) => a.needSocial)),
    mood: stats(agents.map((a) => a.mood)),
    pantryDays: stats(pantries.map((p) => +p.foodDays.toFixed(2))),
    foodInsecureShare: pantries.length ? +(pantries.filter((p) => p.foodDays < 0.5).length / pantries.length).toFixed(3) : 0,
    wallet: { stats: stats(wallets), gini: gini(wallets) },
    carryingShare: agents.length ? +(agents.filter((a) => a.carry > 0).length / agents.length).toFixed(3) : 0,
    injured: tbl<InjuryRow>(conn, 'versonaInjury').filter((i) => i.hp < 100).length,
  };

  // politics
  const econs = tbl<EconRow>(conn, 'settlementEcon');
  const clauses = tbl<ClauseRow>(conn, 'charterClause');
  const norms = tbl<NormRow>(conn, 'settlementNorm');
  const delibs = tbl<DelibRow>(conn, 'versonaDeliberation');
  const rivalries = tbl<RivalryRow>(conn, 'settlementRivalry');
  const factionOf = (a: AgentRow): string =>
    a.job === 'merchant' || a.wallet >= 60n ? 'commerce'
      : a.job === 'guard' || a.job === 'scout' || a.sociability >= 75 ? 'civic' : 'labor';
  const opinionRows = tbl<{ versonaId: bigint; leanX10: number }>(conn, 'versonaOpinion');
  const politics: CensusReport['politics'] = {
    mayoredShare: econs.length ? +(econs.filter((e) => e.mayorId !== 0n).length / econs.length).toFixed(3) : 0,
    taxPctHist: hist(econs.map((e) => `${Math.round(e.taxBps / 100)}%`)),
    treasury: stats(econs.map((e) => Number(e.treasury))),
    clauses: hist(clauses.map((c) => `${c.kind}/${c.status}`)),
    norms: { active: norms.filter((n) => n.active).length, adoption: stats(norms.map((n) => n.adoptionX10 / 10)) },
    delibs: hist(delibs.map((d) => `${d.kind}/${d.status}`)),
    rivalries: { n: rivalries.length, grievance: stats(rivalries.map((r) => r.grievance)) },
    wars: hist(tbl<WarRow>(conn, 'warState').map((w) => w.status)),
    officesFilled: tbl<OfficeRow>(conn, 'settlementOffice').length,
    factions: hist(agents.map(factionOf)),
    opinions: {
      rows: opinionRows.length,
      lean: stats(opinionRows.map((o) => o.leanX10 / 10)),
      strongConvictions: opinionRows.filter((o) => Math.abs(o.leanX10) >= 400).length,
    },
    treaties: hist(tbl<{ kind: string; status: string }>(conn, 'settlementTreaty').map((tr) => `${tr.kind}/${tr.status}`)),
    goodwill: (() => {
      const rows = tbl<{ goodwill: number }>(conn, 'settlementGoodwill');
      return { pairs: rows.length, level: stats(rows.map((r) => r.goodwill)) };
    })(),
    parties: (() => {
      const rows = tbl<{ faction: string; strongCount: number }>(conn, 'settlementParty');
      return { n: rows.length, byFaction: hist(rows.map((p) => p.faction)), strong: stats(rows.map((p) => p.strongCount)) };
    })(),
    recalls: hist(tbl<{ status: string }>(conn, 'recallPetition').map((p) => p.status)),
  };

  // groups
  const households = tbl<HouseholdRow>(conn, 'household');
  const members = tbl<HouseholdMemberRow>(conn, 'householdMember');
  const perHousehold = new Map<bigint, number>();
  for (const m of members) perHousehold.set(m.householdId, (perHousehold.get(m.householdId) ?? 0) + 1);
  const migrations = tbl<MigrationRow>(conn, 'migrationLog');
  const groups: CensusReport['groups'] = {
    households: { n: households.length, size: stats([...perHousehold.values()]) },
    kinship: hist(tbl<KinshipRow>(conn, 'kinship').map((k) => k.kind)),
    migrations: hist(migrations.map((m) => m.reason)),
    civSplit: hist(tbl<CivRow>(conn, 'settlementCiv').map((c) => c.civ)),
    playerResidents: tbl(conn, 'residency').length,
    colonists: migrations.filter((m) => m.reason === 'colony').length,
  };

  // information transport
  const memories = tbl<MemoryRow>(conn, 'versonaMemory');
  const byFact = new Map<string, { holders: Set<bigint>; rumor: Set<bigint> }>();
  for (const m of memories) {
    if (!m.factKey) continue;
    let e = byFact.get(m.factKey);
    if (!e) { e = { holders: new Set(), rumor: new Set() }; byFact.set(m.factKey, e); }
    e.holders.add(m.versonaId);
    if (m.kind === 'rumor') e.rumor.add(m.versonaId);
  }
  const breadths = [...byFact.values()].map((e) => e.holders.size);
  const chats = tbl<ChatRow>(conn, 'versonaChat');
  const playerChats = chats.filter((c) => c.speaker === 'player');
  const standings = tbl<StandingRow>(conn, 'playerStanding');
  const information: CensusReport['information'] = {
    memories: { total: memories.length, kinds: hist(memories.map((m) => m.kind)), importance: stats(memories.map((m) => m.importance)) },
    rumorShare: memories.length ? +(memories.filter((m) => m.kind === 'rumor').length / memories.length).toFixed(3) : 0,
    propagation: {
      breadth: stats(breadths),
      top: [...byFact.entries()].sort((a, b) => b[1].holders.size - a[1].holders.size).slice(0, 8)
        .map(([factKey, e]) => ({ factKey: factKey.slice(0, 60), holders: e.holders.size, rumorHolders: e.rumor.size })),
    },
    playerFacing: {
      standingRows: standings.length,
      regard: stats(standings.map((s) => s.regard)),
      chats: playerChats.length,
      chatAnsweredShare: playerChats.length ? +(chats.filter((c) => c.speaker === 'player' && c.answered).length / playerChats.length).toFixed(3) : 0,
    },
    deception: {
      slanders: memories.filter((m) => m.factKey.startsWith('slander_')).length,
      caughtLying: memories.filter((m) => m.factKey === 'caught_lying').length,
      playerTales: memories.filter((m) => m.factKey.startsWith('tale_')).length,
    },
  };

  return { atMs: nowMs, population, social, emotions, survival, politics, groups, information };
}

/** Extra subscriptions the census needs beyond labBaseSubs(). All small tables. */
export function censusExtraSubs(): string[] {
  return [
    'SELECT * FROM household', 'SELECT * FROM household_member', 'SELECT * FROM kinship',
    'SELECT * FROM migration_log', 'SELECT * FROM grave', 'SELECT * FROM settlement_office',
    'SELECT * FROM settlement_norm', 'SELECT * FROM charter_clause', 'SELECT * FROM versona_deliberation',
    'SELECT * FROM settlement_rivalry', 'SELECT * FROM war_state', 'SELECT * FROM player_standing',
    'SELECT * FROM residency', 'SELECT * FROM versona_injury', 'SELECT * FROM settlement_civ', 'SELECT * FROM versona_origin',
    'SELECT * FROM versona_opinion',
    'SELECT * FROM settlement_goodwill', 'SELECT * FROM settlement_treaty',
    'SELECT * FROM settlement_party', 'SELECT * FROM recall_petition',
  ];
}

// ─── Rendering ───────────────────────────────────────────────────────────────

function kv(rows: Record<string, number>, topN = 12): string {
  return Object.entries(rows).sort((a, b) => b[1] - a[1]).slice(0, topN).map(([k, v]) => `${k}×${v}`).join(' · ') || '—';
}
const st = (s: Stats): string => `n ${s.n} · mean ${s.mean} · p50 ${s.p50} · p90 ${s.p90} · max ${s.max}`;

export function renderCensusMd(c: CensusReport): string {
  const L: string[] = [];
  L.push('# Versona world census');
  L.push('');
  L.push(`Taken ${new Date(c.atMs).toISOString()} — ${c.population.agents} versonas across ${c.population.settlements} settlements.`);
  L.push('');
  L.push('## Population');
  L.push(`- jobs: ${kv(c.population.jobs)}`);
  L.push(`- generations (lineage rows): ${kv(c.population.generations)} · born in-world & alive: ${c.population.bornInWorld}`);
  L.push(`- graves by cause: ${kv(c.population.graves)}`);
  L.push('');
  L.push('## Social network');
  L.push(`- relationships (pairs): ${c.social.pairsTotal} · degree/agent: ${st(c.social.degree)}`);
  L.push(`- familiarity bands: ${kv(c.social.familiarityHist)} · pairs at 100: ${(c.social.maxedShare * 100).toFixed(1)}%`);
  L.push(`- close ties (≥80)/agent: ${st(c.social.closeTiesPerAgent)}`);
  L.push(`- friends (≥50)/agent: ${st(c.social.friendsPerAgent)}`);
  L.push(`- live encounters: ${c.social.encountersLive} (${kv(c.social.encounterTiers)})`);
  L.push(`- favor edges: ${c.social.favorEdges} (${c.social.openDebts} open debts)`);
  L.push('');
  L.push('## Emotions');
  L.push(`- episodes: ${c.emotions.episodes} across ${c.emotions.agentsFeeling} feeling agents (per feeler: ${st(c.emotions.perFeelingAgent)})`);
  L.push(`- types: ${kv(c.emotions.etypeHist, 22)}`);
  L.push(`- targeted (about someone): ${(c.emotions.targetedShare * 100).toFixed(1)}% · resolutions (hope/fear settled): ${(c.emotions.resolutionShare * 100).toFixed(1)}% · intensity: ${st(c.emotions.intensity)}`);
  L.push(`- PAD mood (${c.emotions.pad.rows} rows): P ${st(c.emotions.pad.pleasure)} · A ${st(c.emotions.pad.arousal)} · D ${st(c.emotions.pad.dominance)}`);
  L.push(`- grudges: ${c.emotions.grudges.total} held by ${c.emotions.grudges.holders} agents · intensity ${st(c.emotions.grudges.intensity)} · max offenses ${c.emotions.grudges.maxOffenses}`);
  L.push(`- agents mid hunger-streak: ${c.emotions.hungerStreakActive}`);
  L.push('');
  L.push('## Survival');
  L.push(`- hunger ${st(c.survival.hunger)} · rest ${st(c.survival.needRest)} · social ${st(c.survival.needSocial)} · mood ${st(c.survival.mood)}`);
  L.push(`- pantry days: ${st(c.survival.pantryDays)} · food-insecure (<0.5 d): ${(c.survival.foodInsecureShare * 100).toFixed(1)}%`);
  L.push(`- wallets: ${st(c.survival.wallet.stats)} · **Gini ${c.survival.wallet.gini}**`);
  L.push(`- carrying goods: ${(c.survival.carryingShare * 100).toFixed(1)}% · injured: ${c.survival.injured}`);
  L.push('');
  L.push('## Politics');
  L.push(`- settlements with a mayor: ${(c.politics.mayoredShare * 100).toFixed(1)}% · tax rates: ${kv(c.politics.taxPctHist)} · treasuries: ${st(c.politics.treasury)}`);
  L.push(`- charter clauses (kind/status): ${kv(c.politics.clauses, 20)}`);
  L.push(`- norms active: ${c.politics.norms.active} (adoption ${st(c.politics.norms.adoption)})`);
  L.push(`- deliberations (kind/status): ${kv(c.politics.delibs, 15)}`);
  L.push(`- rivalries: ${c.politics.rivalries.n} (grievance ${st(c.politics.rivalries.grievance)}) · wars: ${kv(c.politics.wars) || 'none'}`);
  L.push(`- offices filled: ${c.politics.officesFilled} · factions: ${kv(c.politics.factions)}`);
  L.push(`- persuaded leans: ${c.politics.opinions.rows} rows (${st(c.politics.opinions.lean)}) · strong convictions (|lean| ≥ 40): ${c.politics.opinions.strongConvictions}`);
  L.push(`- treaties (kind/status): ${kv(c.politics.treaties) || 'none'} · goodwill pairs: ${c.politics.goodwill.pairs} (${st(c.politics.goodwill.level)})`);
  L.push(`- parties: ${c.politics.parties.n} (${kv(c.politics.parties.byFaction) || 'none'}; strong ${st(c.politics.parties.strong)}) · recalls: ${kv(c.politics.recalls) || 'none'}`);
  L.push('');
  L.push('## Groups');
  L.push(`- households: ${c.groups.households.n} (size ${st(c.groups.households.size)})`);
  L.push(`- kinship edges: ${kv(c.groups.kinship)} · migrations: ${kv(c.groups.migrations)}`);
  L.push(`- civilizations: ${kv(c.groups.civSplit)} · player residents: ${c.groups.playerResidents}`);
  L.push(`- colony fission: ${c.groups.colonists} settlers have left to found colonies`);
  L.push('');
  L.push('## Information transport');
  L.push(`- memories: ${c.information.memories.total} (${kv(c.information.memories.kinds)}) · importance ${st(c.information.memories.importance)}`);
  L.push(`- second-hand knowledge (rumor share): ${(c.information.rumorShare * 100).toFixed(1)}%`);
  L.push(`- fact propagation breadth (holders per factKey): ${st(c.information.propagation.breadth)}`);
  for (const t of c.information.propagation.top) L.push(`  - "${t.factKey}" → ${t.holders} holders (${t.rumorHolders} via rumor)`);
  L.push(`- player-facing: ${c.information.playerFacing.standingRows} standing rows (regard ${st(c.information.playerFacing.regard)}) · ${c.information.playerFacing.chats} player chats, ${(c.information.playerFacing.chatAnsweredShare * 100).toFixed(0)}% answered`);
  L.push(`- deception: ${c.information.deception.slanders} slanders circulating · ${c.information.deception.caughtLying} liars caught · ${c.information.deception.playerTales} player-planted tales`);
  L.push('');
  return L.join('\n');
}

export function censusToJson(c: CensusReport): string {
  return JSON.stringify(c, (_k, v: unknown) => (typeof v === 'bigint' ? v.toString() : v));
}
